import { type BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { X } from "@phosphor-icons/react";
import { Button, Flex, IconButton, Text, TextArea } from "@radix-ui/themes";
import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { type Rotation, rotatePoint, toCssPercent } from "./rotationUtils";

import { renderCommentAvatar } from "../../../utils/helpers/ai-persona-avatars";

export type CommentComposeBubblePlacement =
  | "belowRight"
  | "belowLeft"
  | "aboveRight"
  | "aboveLeft";

export function computeBubblePlacement(
  bubbleRect: DOMRect,
  viewportRect: DOMRect,
  marginPx = 8,
): CommentComposeBubblePlacement {
  const overflowsRight = bubbleRect.right > viewportRect.right - marginPx;
  const overflowsBottom = bubbleRect.bottom > viewportRect.bottom - marginPx;
  const overflowsLeft = bubbleRect.left < viewportRect.left + marginPx;
  const overflowsTop = bubbleRect.top < viewportRect.top + marginPx;

  if (overflowsRight && overflowsBottom) return "aboveLeft";
  if (overflowsRight) return "belowLeft";
  if (overflowsBottom) return "aboveRight";
  if (overflowsLeft && overflowsTop) return "belowRight";
  if (overflowsLeft) return "belowRight";
  if (overflowsTop) return "belowRight";
  return "belowRight";
}

const PLACEMENT_TRANSFORM: Record<CommentComposeBubblePlacement, string> = {
  belowRight: "translate(8px, 8px)",
  belowLeft: "translate(calc(-100% - 8px), 8px)",
  aboveRight: "translate(8px, calc(-100% - 8px))",
  aboveLeft: "translate(calc(-100% - 8px), calc(-100% - 8px))",
};

export function CommentComposeBubble({
  relX,
  relY,
  rotation,
  scrollRef,
  authorName,
  authorAvatar,
  value,
  onChange,
  onSubmit,
  onCancel,
  isSubmitting = false,
  submitError = null,
  "data-testid": dataTestId = "comment-compose-bubble",
}: {
  relX: number;
  relY: number;
  rotation: Rotation;
  scrollRef: RefObject<HTMLDivElement | null>;
  authorName: string;
  authorAvatar?: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitError?: string | null;
  "data-testid"?: string;
}) {
  const bubbleRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [placement, setPlacement] =
    useState<CommentComposeBubblePlacement>("belowRight");

  const rotated = rotatePoint({ x: relX, y: relY }, rotation);

  const updatePlacement = useCallback(() => {
    const bubbleEl = bubbleRef.current;
    const scrollEl = scrollRef.current;
    if (!bubbleEl || !scrollEl) return;
    const bubbleRect = bubbleEl.getBoundingClientRect();
    const viewportRect = scrollEl.getBoundingClientRect();
    const next = computeBubblePlacement(bubbleRect, viewportRect);
    setPlacement((prev) => (prev === next ? prev : next));
  }, [scrollRef]);

  useLayoutEffect(() => {
    updatePlacement();
  }, [updatePlacement, rotated.x, rotated.y]);

  useEffect(() => {
    const bubbleEl = bubbleRef.current;
    if (!bubbleEl) return;
    const observer = new ResizeObserver(() => updatePlacement());
    observer.observe(bubbleEl);
    return () => observer.disconnect();
  }, [updatePlacement]);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onCancel]);

  return (
    <div
      ref={bubbleRef}
      role="dialog"
      tabIndex={-1}
      aria-label="New comment"
      data-testid={dataTestId}
      data-placement={placement}
      className="pointer-events-auto absolute z-30 w-64 rounded-lg border border-puntt-neutral-gray-6 bg-base-white p-3 shadow-lg outline-none"
      style={{
        left: toCssPercent(rotated.x),
        top: toCssPercent(rotated.y),
        transform: PLACEMENT_TRANSFORM[placement],
      }}
      onPointerDownCapture={(e) => e.stopPropagation()}
      onPointerUpCapture={(e) => e.stopPropagation()}
    >
      <Flex gap="2" align="center" className="mb-2">
        {renderCommentAvatar({
          isAI: false,
          createdBy: { name: authorName, avatar: authorAvatar },
        } as BaseTicketComment)}
        <Text
          size="2"
          weight="medium"
          className="min-w-0 flex-1 truncate text-puntt-neutral-gray-12"
          data-testid="comment-compose-author"
        >
          {authorName}
        </Text>
        <IconButton
          size="1"
          variant="ghost"
          color="gray"
          aria-label="Cancel comment"
          data-testid="comment-compose-cancel"
          onClick={onCancel}
        >
          <X size={14} aria-hidden="true" />
        </IconButton>
      </Flex>
      <TextArea
        ref={textareaRef}
        data-testid="comment-compose-input"
        aria-label="Comment text"
        placeholder="Add a comment…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={isSubmitting}
        className="mb-2 min-h-0 resize-none"
        rows={3}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            void onSubmit();
          }
        }}
      />
      {submitError && (
        <Text
          size="1"
          role="alert"
          data-testid="comment-compose-submit-error"
          className="text-red-600 mb-2"
        >
          {submitError}
        </Text>
      )}
      <Button
        data-testid="comment-compose-submit"
        size="2"
        className="w-full"
        disabled={!value.trim() || isSubmitting}
        onClick={() => void onSubmit()}
      >
        Add Comment
      </Button>
    </div>
  );
}
